import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaCalendarAlt, FaInfoCircle } from 'react-icons/fa';
import { useDebouncedCallback } from 'use-debounce';
import SimpleToggleSwitch from 'common/components/simpleToggleSwitch/SimpleToggleSwitch';
import ListingTable, { ListingTableColumn } from 'common/components/listingTable/ListingTable';
import PaginationWithInfo from 'common/components/pagination/PaginationWithInfo';
import InputSearch from 'common/components/inputSearch/InputSearch';
import PrivacyService from 'api/privacy/PrivacyService';
import { PrivacyVehicleDto } from 'api/privacy/models/PrivacyVehicleDto';
import { PrivacySearchCriteria } from 'api/privacy/models/PrivacySearchCriteria';
import { Paged } from 'api/common/types/Page';
import { DEFAULT_PAGINATION_ITEMS_PER_PAGE } from 'Config';
import ScheduleModal from './ScheduleModal';
import styles from '../PrivacyTab.module.scss';

type Props = {
    onToggle?: () => void;
    // Self-service mode (My Account): scopes to the user's own assets, hides scheduling, manual toggle only.
    selfService?: boolean;
};

const VehiclesPrivacySection: React.FC<Props> = ({ onToggle, selfService = false }: Props) => {
    const { t } = useTranslation();
    const [vehiclesPage, setVehiclesPage] = useState<Paged<PrivacyVehicleDto>>();
    const [criteria, setCriteria] = useState<PrivacySearchCriteria>({
        itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
        page: 1,
        orderColumn: 'v.registration_number',
        orderBy: 'asc'
    });
    const [scheduleModalVehicle, setScheduleModalVehicle] = useState<PrivacyVehicleDto | null>(null);
    const isFirstLoad = useRef(true);

    const getData = useCallback(async () => {
        try {
            const page = await (selfService ? PrivacyService.getMyVehicles(criteria) : PrivacyService.getVehicles(criteria));
            setVehiclesPage(page);
        } catch {
            // Error handled by Http interceptor
        }
    }, [criteria, selfService]);

    useEffect(() => {
        isFirstLoad.current = false;
        getData();
    }, [getData]);

    const onTableFilter = (field: string, isFilterAsc: boolean) => {
        setCriteria({ ...criteria, page: 1, orderBy: isFilterAsc ? 'asc' : 'desc', orderColumn: field });
    };

    const debounced = useDebouncedCallback((value: string) => {
        setCriteria({ ...criteria, page: 1, allIn: value || undefined });
    }, 500);

    const [togglingId, setTogglingId] = useState<string | null>(null);

    const handleTogglePrivacy = async (vehicle: PrivacyVehicleDto) => {
        if (togglingId) return;
        setTogglingId(vehicle.vehicleId);
        try {
            if (selfService) {
                await PrivacyService.toggleMyPrivacy(vehicle.vehicleId, !vehicle.privacyEnabled);
            } else {
                await PrivacyService.togglePrivacy({
                    entityId: vehicle.vehicleId,
                    entityType: 'vehicle',
                    enabled: !vehicle.privacyEnabled,
                });
            }
            await getData();
            onToggle?.();
        } catch {
            // Error handled by Http interceptor
        } finally {
            setTogglingId(null);
        }
    };

    const handleScheduleSaved = () => {
        setScheduleModalVehicle(null);
        getData();
    };

    // Self-service esconde o calendário, por isso "Ativos" conta só o toggle manual (bate com os switches visíveis).
    // Em Configurações conta também o agendamento ativo (hasLiveSchedule), pois aí o calendário é visível.
    const activeCount = useMemo(() => vehiclesPage?.items.filter(v => selfService ? v.privacyEnabled : (v.privacyEnabled || v.hasLiveSchedule)).length ?? 0, [vehiclesPage, selfService]);

    const tableColumns: ListingTableColumn<PrivacyVehicleDto>[] = [
        {
            name: t('vehicle.registration') as string,
            searchField: 'v.registration_number',
            onSearch: onTableFilter,
            renderCell: row => <>{row.registration}</>
        },
        {
            name: t('vehicle.brand') as string,
            searchField: 'vb.name',
            onSearch: onTableFilter,
            renderCell: row => <>{row.brand || '-'}</>
        },
        {
            name: t('vehicle.model') as string,
            searchField: 'vm.name',
            onSearch: onTableFilter,
            renderCell: row => <>{row.model || '-'}</>
        },
        ...(selfService ? [] : [{
            name: t('privacy.schedule') as string,
            width: '100px',
            cellAlignment: 'center' as const,
            preventClick: true,
            renderCell: (row: PrivacyVehicleDto) => (
                <button
                    className={`${styles.scheduleBtn} ${row.hasLiveSchedule ? styles.scheduleBtnActive : ''}`}
                    type="button"
                    onClick={() => setScheduleModalVehicle(row)}
                    title={t('privacy.schedule') as string}
                >
                    <FaCalendarAlt />
                </button>
            )
        }]),
        {
            name: t('privacy.manual') as string,
            width: '100px',
            cellAlignment: 'center',
            preventClick: true,
            renderCell: row => (
                <SimpleToggleSwitch
                    checked={row.privacyEnabled}
                    onChange={() => handleTogglePrivacy(row)}
                />
            )
        }
    ];

    return (
        <>
            <div className={styles.card}>
                <div className={styles.cardHeader}>
                    <div>
                        <div className={styles.cardTitle}>
                            {t('privacy.vehicles_privacy_title') as string}
                            <FaInfoCircle
                                className={styles.titleInfoIcon}
                                title={(selfService ? t('privacy.account_tooltip') : t('privacy.manual_tooltip')) as string}
                            />
                        </div>
                        <div className={styles.cardDesc}>{t('privacy.vehicles_privacy_desc') as string}</div>
                    </div>
                    <div className={styles.cardMeta}>
                        <div className={styles.cardStats}>
                            <div className={styles.statItem}>
                                <div className={styles.statLabel}>{t('privacy.active') as string}</div>
                                <div className={styles.statVal}>{activeCount}</div>
                            </div>
                            <div className={styles.statDivider} />
                            <div className={styles.statItem}>
                                <div className={styles.statLabel}>{t('privacy.total') as string}</div>
                                <div className={styles.statVal}>{vehiclesPage?.totalItems ?? 0}</div>
                            </div>
                        </div>
                        <InputSearch
                            onChangeValue={debounced}
                            placeholder={t('privacy.search_vehicle') as string}
                        />
                    </div>
                </div>
                <div className={styles.cardBodyNoPadding}>
                    <ListingTable
                        columns={tableColumns}
                        rows={vehiclesPage?.items || []}
                        allowHover={true}
                        initialSearch={{ colField: criteria.orderColumn ?? 'v.registration_number', isOrderAsc: criteria.orderBy === 'asc' }}
                    />
                    {vehiclesPage && <PaginationWithInfo
                        itemName={t('privacy.section_vehicles') as string}
                        currentPage={vehiclesPage.currentPage || 1}
                        pageItems={vehiclesPage.items.length || 0}
                        totalItems={vehiclesPage.totalItems || 0}
                        onChange={page => setCriteria({ ...criteria, page })}
                    />}
                </div>
            </div>

            {scheduleModalVehicle && (
                <ScheduleModal
                    entityId={scheduleModalVehicle.vehicleId}
                    entityType="vehicle"
                    entityName={scheduleModalVehicle.registration}
                    onClose={() => setScheduleModalVehicle(null)}
                    onSave={handleScheduleSaved}
                />
            )}
        </>
    );
};

export default VehiclesPrivacySection;
